import { useTemplateFormContext } from 'Views/Components/TemplateForm/TemplateFormContext';
import ComboboxSetter from '../Combobox/ComboboxSetter';
import { MultiSelectQuantity, MultiSelectQuantityValueType } from '../MultiSelectQuantity/MultiSelectQuantity';
import { NumberTextField } from '../NumberTextBox/NumberTextBox';
import { TextFieldSetter } from '../TextBox/TextFieldSetter';
import { FormFieldDefinition, SelectValueType } from './TemplateFormTypes';

/**
 * Component for rendering form fields based on a json schema
 */
function TemplateForm() {

	const {
		schema: currentSchema,
		formData,
		errors,
		handleInputChange,
		handleInputBlur,
		isReadOnly: isReadonly,
	} = useTemplateFormContext();

	const renderField = (field: FormFieldDefinition) => {
		const fieldType = field.type;
		const fieldIsRequired = field.validation?.required;
		const fieldError = errors[field.id];
		const fieldValue = formData[field.id];

		switch (field.type) {
			case 'text':
				return (
					<TextFieldSetter
						label={field.label}
						placeholder={field.placeholder}
						isRequired={fieldIsRequired}
						value={fieldValue as string | undefined}
						setValue={newValue => handleInputChange(field, newValue)}
						onChangeAndBlur={newValue => handleInputBlur(field, newValue.target.value)}
						errors={fieldError}
						defaultToUndefined
						isDisabled={isReadonly}
					/>
				)
			// Future - migrate to NumberTextFieldSetter (doesn't exists yet. Create new component that works on react state instead of mobx)
			case 'number':
				return (
					<NumberTextField
						model={formData}
						name={field.id}
						modelProperty={field.id}
						label={field.label}
						onAfterChange={evt => handleInputChange(field, evt.target.value)}
						onChangeAndBlur={newValue => handleInputBlur(field, newValue.target.value)}
						placeholder={field.placeholder}
						errors={fieldError}
						isRequired={fieldIsRequired}
						defaultToUndefined
						isDisabled={isReadonly}
					/>
				);
			case 'select':
				// Always show sorted options
				let options = (field.options ?? []);
				options = options.sort((a, b) => (a.value ?? "").localeCompare(b.value ?? ""));
				
				return (
					<ComboboxSetter
						onChange={(evt, data) => {
							const selectedOptionValue = options.find(x => x.value == data.value);
							handleInputChange(field, selectedOptionValue);
							handleInputBlur(field, selectedOptionValue);
						}}
						
						// Tech debt: onAfterSet not working and our Combobox is very nested
						// HandleInputBlur should be  called onAfterSet
						// onAfterSet={(evt, data) => { }}
						isRequired={fieldIsRequired}
						errors={fieldError}
						value={(fieldValue as SelectValueType | undefined)?.value}
						placeholder={field.placeholder}
						getOptionValue={optValue => optValue as string | undefined}
						label={field.label}
						options={options}
						isDisabled={isReadonly}
					/>
				)
			case 'multi-select-quantity':

				let optionGroupLabel = undefined;
				if (field.validation?.maxTotalQuantity != null) optionGroupLabel = `Select at most ${field.validation.maxTotalQuantity} quantities`;
				return (
					<MultiSelectQuantity
						value={fieldValue as MultiSelectQuantityValueType ?? new MultiSelectQuantityValueType()}
						onChange={newValue => {
							handleInputChange(field, newValue);
							handleInputBlur(field, newValue)
						}}
						options={field.options}
						label={field.label}
						optionGroupLabel={optionGroupLabel}
						isRequired={fieldIsRequired}
						errors={fieldError}
						isDisabled={isReadonly}
					/>
				)
			default: return <p>Unsupported field type '{fieldType}'</p>
		}
	};

	return (
		<div className="layout__vertical">
			{currentSchema.map(field => <div key={field.id}>{renderField(field)}</div>)}
		</div>
	);
};

export default TemplateForm;